import { VITE_MCP_URL } from '@constants';
import * as Sentry from '@sentry/react';
import forge from 'apis/forge';
import Modal from 'components/Modal';
import ColorButton from 'components/Widget/Button/ColorButton';
import { putTextOnClipboard } from 'clipboard';
import { CreateMcpTokenResponse } from 'dragonmouse-shared/api/forge';
import { useState } from 'react';
import { toast } from 'react-toastify';
import {
	claudeCodeCommand,
	codexConfigSnippet,
	codexConfigSnippetEnvVar,
	codexEnvVarExport,
	cursorConfigJson,
	cursorDeepLink,
	otherPrompt,
} from 'utils/mcpInstall';
import * as S from './styles';

type Tool = 'claude' | 'codex' | 'cursor' | 'other';

type Props = {
	onClose: () => void;
	onTokenCreated: () => void;
};

const TOOL_LABELS: Record<Tool, string> = {
	claude: 'Claude Code',
	codex: 'Codex',
	cursor: 'Cursor',
	other: 'Other',
};

const ConnectModal = ({ onClose, onTokenCreated }: Props) => {
	const [tool, setTool] = useState<Tool>('claude');
	const [name, setName] = useState<string>(TOOL_LABELS.claude);
	const [nameEdited, setNameEdited] = useState(false);
	const [minting, setMinting] = useState(false);
	const [token, setToken] = useState<CreateMcpTokenResponse | null>(null);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onToolChange = (next: Tool) => {
		setTool(next);
		if (!nameEdited) setName(TOOL_LABELS[next]);
	};

	const onNameChange = (value: string) => {
		setName(value);
		setNameEdited(true);
	};

	const onGenerate = async () => {
		if (minting) return;
		const trimmed = name.trim();
		if (!trimmed) return;
		setMinting(true);
		try {
			const resp = await forge.createMcpToken(trimmed);
			setToken(resp);
			onTokenCreated();
		} catch (err) {
			Sentry.captureException(err);
		} finally {
			setMinting(false);
		}
	};

	const onCopy = async (text: string) => {
		try {
			await putTextOnClipboard(text);
			toast.success('Copied to clipboard');
		} catch {
			toast.error('Could not copy. Select and copy manually.');
		}
	};

	const onInstallCursor = (plaintext: string) => {
		// Must fire on the user gesture, not inside an async callback,
		// or browsers may block the protocol handler.
		window.location.href = cursorDeepLink(plaintext);
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderSnippet = (text: string) => (
		<S.SnippetWrap>
			<S.Snippet>{text}</S.Snippet>
			<S.CopyInside onClick={() => onCopy(text)}>Copy</S.CopyInside>
		</S.SnippetWrap>
	);

	const renderForm = () => (
		<S.Body>
			<S.FieldLabel htmlFor="mcp-tool">Tool</S.FieldLabel>
			<S.Select id="mcp-tool" value={tool} onChange={(e) => onToolChange(e.target.value as Tool)}>
				<option value="claude">Claude Code</option>
				<option value="codex">Codex</option>
				<option value="cursor">Cursor</option>
				<option value="other">Other</option>
			</S.Select>
			<S.FieldLabel htmlFor="mcp-name">Token name</S.FieldLabel>
			<S.Input
				id="mcp-name"
				value={name}
				onChange={(e) => onNameChange(e.target.value)}
				maxLength={100}
				placeholder="What is this token for?"
			/>
		</S.Body>
	);

	const renderReveal = () => {
		if (!token) return null;
		const warning = (
			<S.Warning>
				You won&apos;t see this token again. If you lose it, revoke it and generate a new one.
			</S.Warning>
		);

		if (tool === 'claude') {
			return (
				<S.Body>
					<S.Instruction>
						Run this in your terminal. Claude Code will register the Moonchild MCP server and
						authenticate using the embedded token.
					</S.Instruction>
					{renderSnippet(claudeCodeCommand(token.plaintext))}
					{warning}
				</S.Body>
			);
		}
		if (tool === 'codex') {
			return (
				<S.Body>
					<S.Instruction>
						Add this block to <code>~/.codex/config.toml</code> and restart Codex.
					</S.Instruction>
					{renderSnippet(codexConfigSnippet(token.plaintext))}
					<S.Disclosure>
						<summary>Committing your dotfiles?</summary>
						<S.DisclosureBody>
							<S.Instruction>
								Use this config block instead, then add the export to your shell profile
								(<code>~/.zshrc</code> or <code>~/.bashrc</code>) and start Codex from a fresh
								terminal.
							</S.Instruction>
							{renderSnippet(codexConfigSnippetEnvVar())}
							{renderSnippet(codexEnvVarExport(token.plaintext))}
						</S.DisclosureBody>
					</S.Disclosure>
					{warning}
				</S.Body>
			);
		}
		if (tool === 'cursor') {
			return (
				<S.Body>
					<S.Instruction>
						One-click install opens Cursor and registers the Moonchild MCP server. If your protocol
						handler isn&apos;t set up, copy the manual config instead.
					</S.Instruction>
					<ColorButton
						label="Install in Cursor"
						fullWidth
						onClick={() => onInstallCursor(token.plaintext)}
					/>
					{renderSnippet(cursorConfigJson(token.plaintext))}
					{warning}
				</S.Body>
			);
		}
		const manual = `URL: ${VITE_MCP_URL}\nAuthorization: Bearer ${token.plaintext}`;
		return (
			<S.Body>
				<S.Instruction>
					Paste this prompt into your AI tool — the model will walk you through setup for whichever
					MCP client you use.
				</S.Instruction>
				{renderSnippet(otherPrompt(token.plaintext))}
				<S.FieldLabel>Or configure manually</S.FieldLabel>
				{renderSnippet(manual)}
				{warning}
			</S.Body>
		);
	};

	const isForm = !token;
	const title = isForm ? 'Connect Moonchild MCP' : 'Your new token';

	return (
		<Modal
			title={title}
			onClose={onClose}
			onSubmit={isForm ? onGenerate : onClose}
			submitLabel={isForm ? 'Generate token' : 'Done'}
			disabled={isForm && !name.trim()}
			loading={minting}
		>
			{isForm ? renderForm() : renderReveal()}
		</Modal>
	);
};

export default ConnectModal;
